// src/components/Budget/InviteCodeDisplay.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Chip,
  Alert,
  Snackbar,
  useTheme,
  alpha,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

export const InviteCodeDisplay = ({ open, onClose, inviteCode, expiryDate, onRegenerate }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  // Calculate time remaining until expiry
  useEffect(() => {
    if (!expiryDate) return;

    const updateTimeRemaining = () => {
      const now = new Date();
      const expiry = new Date(expiryDate);
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeRemaining(`${days} day${days > 1 ? 's' : ''} ${hours}h`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours} hour${hours > 1 ? 's' : ''} ${minutes}m`);
      } else {
        setTimeRemaining(`${minutes} minute${minutes > 1 ? 's' : ''}`);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [expiryDate]);

  // Copy invite code to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Share via Web Share API (if available)
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join my shared budget category',
          text: `Use this code to join: ${inviteCode}`,
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      handleCopy();
    }
  };

  const isExpired = timeRemaining === 'Expired';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundImage: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShareIcon color="primary" />
              <Typography variant="h6" fontWeight="600">
                Invite Code Created
              </Typography>
            </Stack>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3}>
            {/* Success message */}
            <Alert severity="success" icon={<CheckIcon />}>
              Your shared category has been created successfully!
            </Alert>

            {/* Invite code display */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                textTransform="uppercase"
                fontWeight="600"
                gutterBottom
                display="block"
              >
                Share this code with others
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  border: `2px solid ${theme.palette.primary.main}`,
                  borderRadius: 2,
                  position: 'relative',
                }}
              >
                <Typography
                  variant="h2"
                  fontWeight="700"
                  letterSpacing={8}
                  sx={{
                    fontFamily: 'monospace',
                    color: theme.palette.primary.main,
                    userSelect: 'all',
                  }}
                >
                  {inviteCode}
                </Typography>
                
                {/* Copy button overlay */}
                <Tooltip title="Copy code">
                  <IconButton
                    onClick={handleCopy}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: alpha(theme.palette.background.paper, 0.9),
                      '&:hover': {
                        backgroundColor: theme.palette.background.paper,
                      },
                    }}
                    size="small"
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            </Box>

            {/* Expiry information */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 2,
                backgroundColor: isExpired
                  ? alpha(theme.palette.error.main, 0.1)
                  : alpha(theme.palette.info.main, 0.1),
                borderRadius: 1,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <ScheduleIcon
                  color={isExpired ? 'error' : 'info'}
                  fontSize="small"
                />
                <Typography variant="body2" fontWeight="500">
                  {isExpired ? 'Code Expired' : 'Time Remaining'}
                </Typography>
              </Stack>
              <Chip
                label={timeRemaining}
                size="small"
                color={isExpired ? 'error' : 'info'}
                sx={{ fontWeight: 600 }}
              />
            </Stack>

            <Divider />

            {/* Instructions */}
            <Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                How to share:
              </Typography>
              <Stack spacing={1} component="ol" sx={{ pl: 2, m: 0 }}>
                <Typography variant="body2" color="text.secondary" component="li">
                  Copy the code above
                </Typography>
                <Typography variant="body2" color="text.secondary" component="li">
                  Send it to the person you want to add
                </Typography>
                <Typography variant="body2" color="text.secondary" component="li">
                  They can join by entering the code in their app
                </Typography>
                <Typography variant="body2" color="text.secondary" component="li">
                  Code expires in 7 days
                </Typography>
              </Stack>
            </Box>

            {/* Regenerate option for expired codes */}
            {isExpired && onRegenerate && (
              <Alert severity="warning">
                This invite code has expired. Generate a new one to invite more members.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ width: '100%' }}
          >
            {isExpired && onRegenerate ? (
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={onRegenerate}
                fullWidth
              >
                Generate New Code
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleCopy}
                  fullWidth
                >
                  Copy Code
                </Button>
                {navigator.share && (
                  <Button
                    variant="contained"
                    startIcon={<ShareIcon />}
                    onClick={handleShare}
                    fullWidth
                  >
                    Share
                  </Button>
                )}
              </>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Copy success snackbar */}
      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
          Invite code copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

InviteCodeDisplay.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  inviteCode: PropTypes.string.isRequired,
  expiryDate: PropTypes.string,
  onRegenerate: PropTypes.func,
};

export default InviteCodeDisplay;
